import React, { useState, useEffect } from 'react';
import api from '../../../utils/Url';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  TextField,
  Autocomplete,
  Collapse,
  Divider,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
interface ApprovedData {
  fileID: number;
  fileName: string;
  fileNo: string;
  synopsis: string;
  rfid: string;
  chackNo: string;
  currentLocation: string;
}

interface Zone {
  zoneID: number;
  zoneName: string;
  zoneCode: string;
  isActive: boolean;
}

interface Ward {
  wardID: number;
  wardName: string;
  wardCode: string;
  zoneID: number;
  zoneName: string;
}

interface FileDetail {
  fileID: number;
  fileNo: string;
  fileName: string;
  rfid: string;
  chackNo: string;
  fileTypeName: string;
  fileGroupName: string;
  fileCategoryName: string;
  fileSubjectName: string;
  className: string;
}

interface FileDocument {
  fileID: number;
  fileName: string;
  fileNo: string;
  fileType: number;
  fileGroup: number;
  fileCategory: number;
  fileClass: number;
  fileSubject: number;
  synopsis: string;
  remark: string;
  refID: number;
  totalPages: number;
  fromPage: number;
  toPage: number;
  status: string;
  originLocation: string;
  currentLocation: string;
  zoneID: number;
  wardID: number;
  createdDate: string;
  createdBy: string;
  updatedBy: string;
  updatedDate: string;
  rfid: string;
  senderID: string;
  receiverID: string;
  fileDoc: string; // This will contain the base64 encoded file
}

interface TotalEPCPendingProps {
  open: boolean;
  onClose: () => void;
}

const TotalEPCPending: React.FC<TotalEPCPendingProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvedData, setApprovedData] = useState<ApprovedData[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [fileDetails, setFileDetails] = useState<FileDetail[]>([]);
  const [filters, setFilters] = useState({
    fileNo: '',
    zoneID: -1,
    wardID: -1,
    rfid: '',
    chackNo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingWards, setLoadingWards] = useState(false);
  const [loadingFileDetails, setLoadingFileDetails] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
    const [fileDocument, setFileDocument] = useState<FileDocument | null>(null);
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [fileLoading, setFileLoading] = useState(false)

  useEffect(() => {
    if (open) {
      fetchZones();
      fetchWards();
      fetchFileDetails();
      fetchApprovedData();
      setShowFilters(false);
      setFilters({
        fileNo: '',
        zoneID: -1,
        wardID: -1,
        rfid: '',
        chackNo: ''
      });
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchApprovedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchApprovedData = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        fileID: -1,
        fileNo: filters.fileNo,
        zoneID: filters.zoneID,
        wardID: filters.wardID,
        rfid: filters.rfid,
        chackNo: filters.chackNo
      };
      const response = await api.post('Dashboard/GetPendingDataShow', payload);
      if (response.data.isSuccess && response.data.data) {
        setApprovedData(response.data.data);
      } else {
        throw new Error(response.data.mesg || t("text.FailedToFetchPendingData"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("text.AnUnknownErrorOccurred"));
    } finally {
      setLoading(false);
    }
  };

  const fetchFileDetails = async () => {
    setLoadingFileDetails(true);
    try {
      const response = await api.post('FileDetail/GetFileDetails', {
        fileID: -1,
        fileType: -1,
        fileGroup: -1,
        fileCategory: -1,
        fileClass: -1,
        fileSubject: -1
      });
      if (response.data.isSuccess && response.data.data) {
        setFileDetails(response.data.data);
      }
    } finally {
      setLoadingFileDetails(false);
    }
  };

  const fetchZones = async () => {
    setLoadingZones(true);
    try {
      const response = await api.post('Zone/GetZonemaster', {
        zoneID: -1,
        user_ID: 1,
        isActive: true
      });
      if (response.data.isSuccess && response.data.data) {
        setZones(response.data.data);
      }
    } finally {
      setLoadingZones(false);
    }
  };

  const fetchWards = async () => {
    setLoadingWards(true);
    try {
      const response = await api.post('Ward/GetWardmaster', {
        wardID: -1,
        zoneID: -1,
        user_ID: 1
      });
      if (response.data.isSuccess && response.data.data) {
        setWards(response.data.data);
      }
    } finally {
      setLoadingWards(false);
    }
  };

    const fetchFileDocument = async (fileID: number) => {
    setFileLoading(true);
    try {
      const response = await api.post('FileDetail/GetFileDetails', {
        ZoneID: -1,
        wardID: -1,
        fileID: fileID,
        fileType: -1,
        fileGroup: -1,
        fileCategory: -1,
        fileClass: -1,
        fileSubject: -1
      });
      
      if (response.data.isSuccess && response.data.data && response.data.data.length > 0) {
        setFileDocument(response.data.data[0]);
        setFileModalOpen(true);
      } else {
        throw new Error(t("text.FileContentNotFound"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("text.FailedToLoadFileContent"));
    } finally {
      setFileLoading(false);
    }
  };

  const handleFileNameClick = (fileID: number) => {
    fetchFileDocument(fileID);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({
      fileNo: '',
      zoneID: -1,
      wardID: -1,
      rfid: '',
      chackNo: ''
    });
  };

  const columns: GridColDef[] = [
    { field: 'fileNo', headerName: t("text.FileNo"), minWidth: 120, flex: 1 },
    { field: 'fileName', headerName: t("text.FileName"), minWidth: 180, flex: 1 },
    { field: 'synopsis', headerName: t("text.Synopsis"), minWidth: 120, flex: 1 },
    { field: 'rfid', headerName: t("text.RFID"), minWidth: 150, flex: 1 },
    { field: 'chackNo', headerName: t("text.ChackNo"), minWidth: 120, flex: 1 },
    { field: 'currentLocation', headerName: t("text.CurrentLocation"), flex: 1 },
    {
        field: 'preview',
        headerName: t("text.PreviewFile"),
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            onClick={() => handlePreviewClick(params.row.fileID)}
            color="primary"
            size="small"
            sx={{ '&:hover': { backgroundColor: 'transparent' } }}
          >
            <VisibilityIcon />
          </IconButton>
        )
      }
    ];
      const handlePreviewClick = (fileID: number) => {
      fetchFileDocument(fileID);
    };

      const renderFileContent = () => {
        if (!fileDocument || !fileDocument.fileDoc) {
          return <Typography>{t("text.NoFileContentAvailable")}</Typography>;
        }
    
        const [mimeType, base64Data] = fileDocument.fileDoc.split(';base64,');
        
        if (mimeType.includes('application/pdf')) {
          return (
            <embed 
              src={fileDocument.fileDoc} 
              type="application/pdf" 
              width="100%" 
              height="100%"
              style={{ minHeight: '500px' }}
            />
          );
        } else if (mimeType.includes('image/')) {
          return (
            <img 
              src={fileDocument.fileDoc} 
              alt={fileDocument.fileName}
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            />
          );
        } else {
          return (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">{fileDocument.fileName}</Typography>
              <Typography>{t("text.UnsupportedFileFormat")}</Typography>
              <Button 
                variant="contained" 
                href={fileDocument.fileDoc} 
                download={fileDocument.fileName}
                sx={{ mt: 2 }}
              >
                {t("text.DownloadFile")}
              </Button>
            </Box>
          );
        }
      };


  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '90vh',
          m: fullScreen ? 0 : 2,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white
        }}
      >
        <Typography variant="h6" sx={{ pl: 1 }}>{t("text.PendingEPCData")}</Typography>
        <IconButton onClick={onClose} sx={{ color: theme.palette.common.white }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mb: 1 }}
          >
            {showFilters ? t("text.HideFilters") : t("text.ShowFilters")}
          </Button>

          <Collapse in={showFilters}>
            <Box
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Divider sx={{ mb: 1 }} />

              <Grid container spacing={1}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={Array.from(new Set(fileDetails.map(item => item.fileNo)))}
                    loading={loadingFileDetails}
                    value={filters.fileNo}
                    onChange={(_, newValue) => handleFilterChange('fileNo', newValue || '')}
                    renderInput={(params) => (
                      <TextField {...params} label={t("text.FileNo")} size="small" fullWidth />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={zones}
                    loading={loadingZones}
                    getOptionLabel={(option) => option.zoneName}
                    value={zones.find(z => z.zoneID === filters.zoneID) || null}
                    onChange={(_, newValue) => handleFilterChange('zoneID', newValue?.zoneID || -1)}
                    renderInput={(params) => <TextField {...params} label={t("text.Zone")} size="small" fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={wards}
                    loading={loadingWards}
                    getOptionLabel={(option) => option.wardName}
                    value={wards.find(w => w.wardID === filters.wardID) || null}
                    onChange={(_, newValue) => handleFilterChange('wardID', newValue?.wardID || -1)}
                    renderInput={(params) => <TextField {...params} label={t("text.Ward")} size="small" fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={[...fileDetails.map(item => item.rfid)]}
                    loading={loadingFileDetails}
                    value={filters.rfid}
                    onChange={(_, newValue) => handleFilterChange('rfid', newValue || '')}
                    renderInput={(params) => (
                      <TextField {...params} label={t("text.RFID")} size="small" fullWidth />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={[...fileDetails.map(item => item.chackNo)]}
                    loading={loadingFileDetails}
                    value={filters.chackNo}
                    onChange={(_, newValue) => handleFilterChange('chackNo', newValue || '')}
                    renderInput={(params) => (
                      <TextField {...params} label={t("text.ChackNo")} size="small" fullWidth />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button variant="outlined" onClick={resetFilters} fullWidth>
                    {t("text.Reset")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
          ) : (
            <Box sx={{ height: '100%', width: '100%', flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={approvedData}
                columns={columns}
                getRowId={(row) => row.fileID}
                disableRowSelectionOnClick
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                sx={{
                  height: '100%',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 'bold'
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1 }}>
        <Button onClick={onClose} color="primary" variant="contained" sx={{ minWidth: '100px' }}>
          {t("text.Close")}
        </Button>
      </DialogActions>
    </Dialog>

       {/* File Content Modal */}
      <Dialog
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white
          }}
        >
          <Typography variant="h6" sx={{ pl: 1 }}>
            {fileDocument?.fileName || t("text.FileContent")}
          </Typography>
          <IconButton onClick={() => setFileModalOpen(false)} sx={{ color: theme.palette.common.white }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {fileLoading ? (
            <CircularProgress />
          ) : (
            renderFileContent()
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1 }}>
          <Button 
            onClick={() => setFileModalOpen(false)} 
            color="primary" 
            variant="contained" 
            sx={{ minWidth: '100px' }}
          >
            {t("text.Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TotalEPCPending;
















// import React, { useState, useEffect } from 'react';
// import api from '../../../utils/Url';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Box,
//   Typography,
//   Button,
//   CircularProgress,
//   IconButton,
//   useMediaQuery,
//   useTheme,
//   TextField,
//   Autocomplete,
//   Collapse,
//   Divider,
//   Grid
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import FilterListIcon from '@mui/icons-material/FilterList';
// import { DataGrid, GridColDef } from '@mui/x-data-grid';

// interface ApprovedData {
//   fileID: number;
//   fileName: string;
//   fileNo: string;
//   synopsis: string;
//   rfid: string;
//   chackNo: string;
//   currentLocation: string;
// }

// interface Zone {
//   zoneID: number;
//   zoneName: string;
//   zoneCode: string;
//   isActive: boolean;
// }

// interface Ward {
//   wardID: number;
//   wardName: string;
//   wardCode: string;
//   zoneID: number;
//   zoneName: string;
// }

// interface FileDetail {
//   fileID: number;
//   fileNo: string;
//   fileName: string;
//   rfid: string;
//   chackNo: string;
//   fileTypeName: string;
//   fileGroupName: string;
//   fileCategoryName: string;
//   fileSubjectName: string;
//   className: string;
// }

// interface TotalEPCPendingProps {
//   open: boolean;
//   onClose: () => void;
// }

// const TotalEPCPending: React.FC<TotalEPCPendingProps> = ({ open, onClose }) => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [approvedData, setApprovedData] = useState<ApprovedData[]>([]);
//   const [zones, setZones] = useState<Zone[]>([]);
//   const [wards, setWards] = useState<Ward[]>([]);
//   const [fileDetails, setFileDetails] = useState<FileDetail[]>([]);
//   const [filters, setFilters] = useState({
//     fileNo: '',
//     zoneID: -1,
//     wardID: -1,
//     rfid: '',
//     chackNo: ''
//   });
//   const [showFilters, setShowFilters] = useState(false);
//   const [loadingZones, setLoadingZones] = useState(false);
//   const [loadingWards, setLoadingWards] = useState(false);
//   const [loadingFileDetails, setLoadingFileDetails] = useState(false);
//   const theme = useTheme();
//   const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

//   const [paginationModel, setPaginationModel] = useState({
//     page: 0,
//     pageSize: 10
//   });

//   useEffect(() => {
//     if (open) {
//       fetchZones();
//       fetchWards();
//       fetchFileDetails();
//       fetchApprovedData();
//       setShowFilters(false);
//       setFilters({
//         fileNo: '',
//         zoneID: -1,
//         wardID: -1,
//         rfid: '',
//         chackNo: ''
//       });
//     }
//   }, [open]);

//   useEffect(() => {
//     if (open) {
//       fetchApprovedData();
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [filters]);

//   const fetchApprovedData = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const payload = {
//         fileID: -1,
//         fileNo: filters.fileNo,
//         zoneID: filters.zoneID,
//         wardID: filters.wardID,
//         rfid: filters.rfid,
//         chackNo: filters.chackNo
//       };
//       const response = await api.post('Dashboard/GetPendingDataShow', payload);
//       if (response.data.isSuccess && response.data.data) {
//         setApprovedData(response.data.data);
//       } else {
//         throw new Error(response.data.mesg || 'Failed to fetch pending data');
//       }
//     } catch (err) {
//       setError(err instanceof Error ? err.message : 'An unknown error occurred');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchFileDetails = async () => {
//     setLoadingFileDetails(true);
//     try {
//       const response = await api.post('FileDetail/GetFileDetails', {
//         fileID: -1,
//         fileType: -1,
//         fileGroup: -1,
//         fileCategory: -1,
//         fileClass: -1,
//         fileSubject: -1
//       });
//       if (response.data.isSuccess && response.data.data) {
//         setFileDetails(response.data.data);
//       }
//     } finally {
//       setLoadingFileDetails(false);
//     }
//   };

//   const fetchZones = async () => {
//     setLoadingZones(true);
//     try {
//       const response = await api.post('Zone/GetZonemaster', {
//         zoneID: -1,
//         user_ID: 1,
//         isActive: true
//       });
//       if (response.data.isSuccess && response.data.data) {
//         setZones(response.data.data);
//       }
//     } finally {
//       setLoadingZones(false);
//     }
//   };

//   const fetchWards = async () => {
//     setLoadingWards(true);
//     try {
//       const response = await api.post('Ward/GetWardmaster', {
//         wardID: -1,
//         zoneID: -1,
//         user_ID: 1
//       });
//       if (response.data.isSuccess && response.data.data) {
//         setWards(response.data.data);
//       }
//     } finally {
//       setLoadingWards(false);
//     }
//   };

//   const handleFilterChange = (field: string, value: any) => {
//     setFilters(prev => ({ ...prev, [field]: value }));
//   };

//   const resetFilters = () => {
//     setFilters({
//       fileNo: '',
//       zoneID: -1,
//       wardID: -1,
//       rfid: '',
//       chackNo: ''
//     });
//   };

//   const columns: GridColDef[] = [
//     { field: 'fileNo', headerName: 'File No', minWidth: 120, flex: 1 },
//     { field: 'fileName', headerName: 'File Name', minWidth: 180, flex: 1 },
//     { field: 'synopsis', headerName: 'Synopsis', minWidth: 120, flex: 1 },
//     { field: 'rfid', headerName: 'RFID', minWidth: 150, flex: 1 },
//     { field: 'chackNo', headerName: 'Chack No', minWidth: 120, flex: 1 },
//     { field: 'currentLocation', headerName: 'Current Location', flex: 1 }
//   ];

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       fullScreen={fullScreen}
//       maxWidth="xl"
//       fullWidth
//       PaperProps={{
//         sx: {
//           height: fullScreen ? '100%' : '90vh',
//           m: fullScreen ? 0 : 2,
//           display: 'flex',
//           flexDirection: 'column'
//         }
//       }}
//     >
//       <DialogTitle
//         sx={{
//           p: 1,
//           display: 'flex',
//           justifyContent: 'space-between',
//           alignItems: 'center',
//           backgroundColor: theme.palette.primary.main,
//           color: theme.palette.common.white
//         }}
//       >
//         <Typography variant="h6" sx={{ pl: 1 }}>Pending EPC Data</Typography>
//         <IconButton onClick={onClose} sx={{ color: theme.palette.common.white }}>
//           <CloseIcon />
//         </IconButton>
//       </DialogTitle>

//       <DialogContent dividers sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
//         <Box sx={{ mb: 1 }}>
//           <Button
//             variant="outlined"
//             startIcon={<FilterListIcon />}
//             onClick={() => setShowFilters(!showFilters)}
//             sx={{ mb: 1 }}
//           >
//             {showFilters ? 'Hide Filters' : 'Show Filters'}
//           </Button>

//           <Collapse in={showFilters}>
//             <Box
//               sx={{
//                 p: 1,
//                 border: '1px solid',
//                 borderColor: 'divider',
//                 borderRadius: 1,
//                 mb: 1,
//                 backgroundColor: theme.palette.background.paper
//               }}
//             >
//               <Divider sx={{ mb: 1 }} />

//               <Grid container spacing={1}>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={Array.from(new Set(fileDetails.map(item => item.fileNo)))}
//                     loading={loadingFileDetails}
//                     value={filters.fileNo}
//                     onChange={(_, newValue) => handleFilterChange('fileNo', newValue || '')}
//                     renderInput={(params) => (
//                       <TextField {...params} label="File No" size="small" fullWidth />
//                     )}
//                     freeSolo
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={zones}
//                     loading={loadingZones}
//                     getOptionLabel={(option) => option.zoneName}
//                     value={zones.find(z => z.zoneID === filters.zoneID) || null}
//                     onChange={(_, newValue) => handleFilterChange('zoneID', newValue?.zoneID || -1)}
//                     renderInput={(params) => <TextField {...params} label="Zone" size="small" fullWidth />}
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={wards}
//                     loading={loadingWards}
//                     getOptionLabel={(option) => option.wardName}
//                     value={wards.find(w => w.wardID === filters.wardID) || null}
//                     onChange={(_, newValue) => handleFilterChange('wardID', newValue?.wardID || -1)}
//                     renderInput={(params) => <TextField {...params} label="Ward" size="small" fullWidth />}
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={[...fileDetails.map(item => item.rfid)]}
//                     loading={loadingFileDetails}
//                     value={filters.rfid}
//                     onChange={(_, newValue) => handleFilterChange('rfid', newValue || '')}
//                     renderInput={(params) => (
//                       <TextField {...params} label="RFID" size="small" fullWidth />
//                     )}
//                     freeSolo
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={[...fileDetails.map(item => item.chackNo)]}
//                     loading={loadingFileDetails}
//                     value={filters.chackNo}
//                     onChange={(_, newValue) => handleFilterChange('chackNo', newValue || '')}
//                     renderInput={(params) => (
//                       <TextField {...params} label="Chack No" size="small" fullWidth />
//                     )}
//                     freeSolo
//                   />
//                 </Grid>
//                 <Grid item xs={6} md={2}>
//                   <Button variant="outlined" onClick={resetFilters} fullWidth>
//                     Reset
//                   </Button>
//                 </Grid>
//               </Grid>
//             </Box>
//           </Collapse>
//         </Box>

//         <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
//           {loading ? (
//             <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
//               <CircularProgress />
//             </Box>
//           ) : error ? (
//             <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
//           ) : (
//             <Box sx={{ height: '100%', width: '100%', flex: 1, minHeight: 0 }}>
//               <DataGrid
//                 rows={approvedData}
//                 columns={columns}
//                 getRowId={(row) => row.fileID}
//                 disableRowSelectionOnClick
//                 pagination
//                 paginationModel={paginationModel}
//                 onPaginationModelChange={setPaginationModel}
//                 pageSizeOptions={[5, 10, 25, 50, 100]}
//                 sx={{
//                   height: '100%',
//                   '& .MuiDataGrid-columnHeaders': {
//                     backgroundColor: theme.palette.primary.main,
//                     color: theme.palette.common.white,
//                     fontWeight: 'bold'
//                   },
//                   '& .MuiDataGrid-virtualScroller': {
//                     overflow: 'auto'
//                   }
//                 }}
//               />
//             </Box>
//           )}
//         </Box>
//       </DialogContent>

//       <DialogActions sx={{ p: 1 }}>
//         <Button onClick={onClose} color="primary" variant="contained" sx={{ minWidth: '100px' }}>
//           Close
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default TotalEPCPending;





















// import React, { useState, useEffect } from 'react';
// import api from '../../../utils/Url';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Box,
//   Typography,
//   Button,
//   CircularProgress,
//   IconButton,
//   useMediaQuery,
//   useTheme,
//   TextField,
//   Autocomplete,
//   Collapse,
//   Divider,
//   Grid
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import FilterListIcon from '@mui/icons-material/FilterList';
// import { DataGrid, GridColDef } from '@mui/x-data-grid';

// interface ApprovedData {
//   fileID: number;
//   fileName: string;
//   fileNo: string;
//   synopsis: string;
//   rfid: string;
//   chackNo: string;
//   currentLocation: string;
// }

// interface Zone {
//   zoneID: number;
//   zoneName: string;
//   zoneCode: string;
//   isActive: boolean;
// }

// interface Ward {
//   wardID: number;
//   wardName: string;
//   wardCode: string;
//   zoneID: number;
//   zoneName: string;
// }

// interface TotalEPCPendingProps {
//   open: boolean;
//   onClose: () => void;
// }

// const TotalEPCPending: React.FC<TotalEPCPendingProps> = ({ open, onClose }) => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [approvedData, setApprovedData] = useState<ApprovedData[]>([]);
//   const [zones, setZones] = useState<Zone[]>([]);
//   const [wards, setWards] = useState<Ward[]>([]);
//   const [filters, setFilters] = useState({
//     fileNo: '',
//     zoneID: -1,
//     wardID: -1,
//     rfid: '',
//     chackNo: ''
//   });
//   const [showFilters, setShowFilters] = useState(false);
//   const [loadingZones, setLoadingZones] = useState(false);
//   const [loadingWards, setLoadingWards] = useState(false);
//   const theme = useTheme();
//   const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

//   const [paginationModel, setPaginationModel] = useState({
//     page: 0,
//     pageSize: 5
//   });

//   useEffect(() => {
//     if (open) {
//       fetchZones();
//       fetchWards();
//       fetchApprovedData();
//     }
//   }, [open]);

//   useEffect(() => {
//     if (open) {
//       fetchApprovedData();
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [filters]);

//   const fetchApprovedData = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const payload = {
//         fileID: -1,
//         fileNo: filters.fileNo,
//         zoneID: filters.zoneID,
//         wardID: filters.wardID,
//         rfid: filters.rfid,
//         chackNo: filters.chackNo
//       };
//       const response = await api.post('Dashboard/GetPendingDataShow', payload);
//       if (response.data.isSuccess && response.data.data) {
//         setApprovedData(response.data.data);
//       } else {
//         throw new Error(response.data.mesg || 'Failed to fetch approved data');
//       }
//     } catch (err) {
//       setError(err instanceof Error ? err.message : 'An unknown error occurred');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchZones = async () => {
//     setLoadingZones(true);
//     try {
//       const response = await api.post('Zone/GetZonemaster', {
//         zoneID: -1,
//         user_ID: 1,
//         isActive: true
//       });
//       if (response.data.isSuccess && response.data.data) {
//         setZones(response.data.data);
//       }
//     } finally {
//       setLoadingZones(false);
//     }
//   };

//   const fetchWards = async () => {
//     setLoadingWards(true);
//     try {
//       const response = await api.post('Ward/GetWardmaster', {
//         wardID: -1,
//         zoneID: -1,
//         user_ID: 1
//       });
//       if (response.data.isSuccess && response.data.data) {
//         setWards(response.data.data);
//       }
//     } finally {
//       setLoadingWards(false);
//     }
//   };

//   const handleFilterChange = (field: string, value: any) => {
//     setFilters(prev => ({ ...prev, [field]: value }));
//   };

//   const resetFilters = () => {
//     setFilters({
//       fileNo: '',
//       zoneID: -1,
//       wardID: -1,
//       rfid: '',
//       chackNo: ''
//     });
//   };

//   const columns: GridColDef[] = [
//     { field: 'fileNo', headerName: 'File No', minWidth: 120, flex: 1 },
//     { field: 'fileName', headerName: 'File Name', minWidth: 180, flex: 1 },
//     { field: 'synopsis', headerName: 'Synopsis', minWidth: 120, flex: 1 },
//     { field: 'rfid', headerName: 'RFID', minWidth: 150, flex: 1 },
//     { field: 'chackNo', headerName: 'Chack No', minWidth: 120, flex: 1 },
//     { field: 'currentLocation', headerName: 'Current Location', flex: 1 }
//   ];

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       fullScreen={fullScreen}
//       maxWidth="xl"
//       fullWidth
//       PaperProps={{
//         sx: {
//           height: fullScreen ? '100%' : '90vh',
//           m: fullScreen ? 0 : 2
//         }
//       }}
//     >
//       <DialogTitle
//         sx={{
//           m: 0,
//           p: 2,
//           display: 'flex',
//           justifyContent: 'space-between',
//           alignItems: 'center',
//           backgroundColor: theme.palette.primary.main,
//           color: theme.palette.common.white
//         }}
//       >
//         <Typography variant="h6">Pending EPC Data</Typography>
//         <IconButton onClick={onClose} sx={{ color: theme.palette.common.white }}>
//           <CloseIcon />
//         </IconButton>
//       </DialogTitle>

//       <DialogContent dividers sx={{ p: 2 }}>
//         <Box mb={2}>
//           <Button
//             variant="outlined"
//             startIcon={<FilterListIcon />}
//             onClick={() => setShowFilters(!showFilters)}
//             sx={{ mb: 2 }}
//           >
//             {showFilters ? 'Hide Filters' : 'Show Filters'}
//           </Button>

//           <Collapse in={showFilters}>
//             <Box
//               sx={{
//                 p: 2,
//                 border: '1px solid',
//                 borderColor: 'divider',
//                 borderRadius: 1,
//                 mb: 2,
//                 backgroundColor: theme.palette.background.paper
//               }}
//             >
//               <Divider sx={{ mb: 2 }} />

//               <Grid container spacing={2}>
//                 <Grid item xs={12} md={3}>
//                   <TextField
//                     label="File No"
//                     value={filters.fileNo}
//                     onChange={(e) => handleFilterChange('fileNo', e.target.value)}
//                     fullWidth
//                     size="small"
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={zones}
//                     loading={loadingZones}
//                     getOptionLabel={(option) => option.zoneName}
//                     value={zones.find(z => z.zoneID === filters.zoneID) || null}
//                     onChange={(_, newValue) => handleFilterChange('zoneID', newValue?.zoneID || -1)}
//                     renderInput={(params) => <TextField {...params} label="Zone" size="small" fullWidth />}
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <Autocomplete
//                     options={wards}
//                     loading={loadingWards}
//                     getOptionLabel={(option) => option.wardName}
//                     value={wards.find(w => w.wardID === filters.wardID) || null}
//                     onChange={(_, newValue) => handleFilterChange('wardID', newValue?.wardID || -1)}
//                     renderInput={(params) => <TextField {...params} label="Ward" size="small" fullWidth />}
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <TextField
//                     label="RFID"
//                     value={filters.rfid}
//                     onChange={(e) => handleFilterChange('rfid', e.target.value)}
//                     fullWidth
//                     size="small"
//                   />
//                 </Grid>
//                 <Grid item xs={12} md={3}>
//                   <TextField
//                     label="Chack No"
//                     value={filters.chackNo}
//                     onChange={(e) => handleFilterChange('chackNo', e.target.value)}
//                     fullWidth
//                     size="small"
//                   />
//                 </Grid>
//                 <Grid item xs={6} md={2}>
//                   <Button variant="outlined" onClick={resetFilters} fullWidth>
//                     Reset
//                   </Button>
//                 </Grid>
//               </Grid>
//             </Box>
//           </Collapse>
//         </Box>

//         {loading ? (
//           <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//             <CircularProgress />
//           </Box>
//         ) : error ? (
//           <Typography color="error">{error}</Typography>
//         ) : (
//           <DataGrid
//             rows={approvedData}
//             columns={columns}
//             getRowId={(row) => row.fileID}
//             autoHeight
//             disableRowSelectionOnClick
//             pagination
//             paginationModel={paginationModel}
//             onPaginationModelChange={setPaginationModel}
//             pageSizeOptions={[5, 10, 25, 50, 100]}
//             sx={{
//               backgroundColor: 'white',
//               border: '1px solid #ccc',
//               '& .MuiDataGrid-columnHeaders': {
//                 backgroundColor: theme.palette.primary.main,
//                 color: theme.palette.common.white,
//                 fontWeight: 'bold'
//               }
//             }}
//           />
//         )}
//       </DialogContent>

//       <DialogActions sx={{ p: 2 }}>
//         <Button onClick={onClose} color="primary" variant="contained" sx={{ minWidth: '100px' }}>
//           Close
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default TotalEPCPending;